import StickyHScroll from "@/components/StickyHScroll";
import { rigaCliccabile, RIGA_CLS } from "@/lib/rigaCliccabile";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ContactRound, Plus, Trash2, ArrowRight, Search } from "lucide-react";
import { DuplicateCfDialog } from "@/components/AnagraficaPicker";
import AnagraficaDatiForm, { validateCfPivaObbligatori } from "@/components/anagrafiche/AnagraficaDatiForm";
import {
  CATEGORIE_LABELS, CATEGORIE_KEYS, TIPO_PERSONA_LABELS, SUB_LABELS,
  summarizeCategorie, isEmptyCategorie, emptyCategorie,
} from "@/lib/anagrafiche";
import { toast } from "sonner";
import { useAuth, isElevated } from "@/contexts/AuthContext";
import { attrRicerca } from "@/lib/listaColonne";

const PAGE_SIZES = [50, 100, 200];

function emptyForm(companyId = "") {
  return {
    tipo_persona: "privato",
    nome: "", cognome: "", ragione_sociale: "",
    codice_fiscale: "", partita_iva: "",
    telefono: "", email: "",
    company_id: companyId,
    categorie: emptyCategorie(),
  };
}

export default function Anagrafiche() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const elev = isElevated(user);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("__all__");
  const [categoriaFilter, setCategoriaFilter] = useState("__all__");
  // Sprint PRIV-SCOPE.3 — selettore azienda per scope multi-tenant
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("__all__");
  const [pageSize, setPageSize] = useState(50);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [taxonomy, setTaxonomy] = useState({});
  const [saving, setSaving] = useState(false);
  // Sprint 4D.1 (B1): modale duplicate CF
  const [dupModal, setDupModal] = useState({ open: false, existing: null });
  // Sprint UTENTI-2A: canManageAccess rimosso (legame anagrafica→utente smontato).

  // Mappa id → nome azienda per render colonna
  const companyMap = useMemo(() => {
    const m = {};
    companies.forEach((c) => { m[c.id] = c.name || c.nome_breve || c.ragione_sociale || c.id; });
    return m;
  }, [companies]);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (tipo !== "__all__") params.set("tipo_persona", tipo);
      if (categoriaFilter !== "__all__") params.set("categoria", categoriaFilter);
      if (companyFilter !== "__all__") params.set("company_id", companyFilter);
      params.set("page_size", String(pageSize));
      const r = await api.get(`/anagrafiche?${params.toString()}`);
      setItems(r.data?.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  useEffect(() => {
    api.get("/anagrafiche/_meta/categorie").then((r) => setTaxonomy(r.data || {})).catch(() => {});
    // Sprint PRIV-SCOPE.3 — carica aziende visibili allo scope utente
    api.get("/companies?page_size=200").then((r) => {
      const items = r.data?.items || r.data || [];
      setCompanies(Array.isArray(items) ? items : []);
    }).catch(() => setCompanies([]));
    load();
    // eslint-disable-next-line
  }, []);

  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, tipo, categoriaFilter, companyFilter, pageSize]);

  // Pre-seleziona company nel form quando filtro è specifico
  useEffect(() => {
    // Sprint SICUREZZA-AMBITO: il backend non assegna più l'azienda d'ufficio; con una sola azienda la si mostra già scelta
    const unica = companies.length === 1 ? companies[0].id : null;
    const pre = companyFilter !== "__all__" ? companyFilter : unica;
    if (openNew && pre && !form.company_id) {
      setForm((f) => ({ ...f, company_id: pre }));
    }
    // eslint-disable-next-line
  }, [openNew]);

  const submit = async (force = false) => {
    if (form.tipo_persona === "privato") {
      if (!form.nome.trim() || !form.cognome.trim()) {
        toast.error("Nome e cognome obbligatori"); return;
      }
    } else if (!form.ragione_sociale.trim()) {
      toast.error("Ragione sociale obbligatoria"); return;
    }
    // Sprint SICUREZZA-AMBITO — company_id sempre obbligatorio
    if (!form.company_id) {
      toast.error("Seleziona l'azienda di appartenenza"); return;
    }
    // Sprint 4D.2: validazione CF/PIVA obbligatori
    const fiscalErr = validateCfPivaObbligatori(form);
    if (fiscalErr) { toast.error(fiscalErr); return; }
    if (isEmptyCategorie(form.categorie)) {
      toast.error("Seleziona almeno 1 categoria"); return;
    }
    setSaving(true);
    try {
      const cleaned = Object.fromEntries(
        Object.entries(form).filter(([k, v]) => {
          if (k === "categorie") return true;
          if (k === "tipo_persona") return true;
          if (k === "company_id") return !!v;
          return v != null && String(v).trim() !== "";
        })
      );
      const url = force ? "/anagrafiche?force_duplicate_cf=true" : "/anagrafiche";
      const r = await api.post(url, cleaned);
      toast.success("Anagrafica creata");
      setOpenNew(false); setForm(emptyForm());
      setDupModal({ open: false, existing: null });
      load();
      // Open detail of newly created
      window.location.href = `/anagrafiche/${r.data.id}`;
    } catch (e) {
      // Sprint 4D.1 (B1): gestione 409 duplicate_codice_fiscale
      const status = e?.response?.status;
      const det = e?.response?.data?.detail;
      if (status === 409 && det && det.error === "duplicate_codice_fiscale") {
        setDupModal({ open: true, existing: det.existing_anagrafica });
      } else {
        const msg = typeof det === "string" ? det : (det?.message || "Errore");
        toast.error(msg);
      }
    } finally { setSaving(false); }
  };

  const labelOf = (a) =>
    a.ragione_sociale || `${a.cognome || ""} ${a.nome || ""}`.trim() || "(senza nome)";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <ContactRound className="h-6 w-6 text-blue-600" /> Anagrafiche
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Anagrafica unificata: clienti, fornitori, utenti interni — categorie multi-livello
          </p>
        </div>
        <div className="flex gap-2">
          {elev && (
            <Link to="/anagrafiche/cestino">
              <Button variant="outline" data-testid="anag-cestino-btn">
                <Trash2 className="h-4 w-4 mr-1.5" /> Cestino
              </Button>
            </Link>
          )}
          <Button onClick={() => setOpenNew(true)} className="bg-blue-600 hover:bg-blue-700"
                  data-testid="anag-new-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nuova anagrafica
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3 border border-slate-200 bg-white">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Cerca</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Codice, nome, ragione, CF, P.IVA, contatti…"
                     className="pl-8" data-testid="anag-search" {...attrRicerca("anagrafiche")} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Tipo persona</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger className="w-44" data-testid="anag-filter-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti</SelectItem>
                {Object.entries(TIPO_PERSONA_LABELS).map(([k, v]) =>
                  <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Categoria</Label>
            <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
              <SelectTrigger className="w-56" data-testid="anag-filter-categoria"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte</SelectItem>
                {CATEGORIE_KEYS.map((k) =>
                  <SelectItem key={k} value={k}>{CATEGORIE_LABELS[k]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Sprint PRIV-SCOPE.3 — filtro azienda */}
          <div>
            <Label className="text-xs">Azienda</Label>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-56" data-testid="anag-filter-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte (mio scope)</SelectItem>
                {companies.map((c) =>
                  <SelectItem key={c.id} value={c.id}>{c.name || c.nome_breve || c.ragione_sociale || c.id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Per pagina</Label>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-sm bg-white">
        <StickyHScroll testid="anagrafiche-hscroll">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-slate-50 border-b text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Codice</th>
                <th className="px-4 py-2.5">Nome / Ragione sociale</th>
                <th className="px-4 py-2.5">Tipo</th>
                <th className="px-4 py-2.5">Categorie</th>
                <th className="px-4 py-2.5">Azienda</th>
                <th className="px-4 py-2.5">Email</th>
                <th className="px-4 py-2.5">Telefono</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((a) => {
                const sum = summarizeCategorie(a.categorie);
                return (
                  <tr key={a.id} {...rigaCliccabile(() => navigate(`/anagrafiche/${a.id}`))}
                      className={`hover:bg-slate-50 ${RIGA_CLS}`} data-testid={`anag-riga-${a.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{a.codice}</td>
                    <td className="px-4 py-3 font-medium text-slate-900 truncate max-w-[220px]">{labelOf(a)}</td>
                    <td className="px-4 py-3 text-slate-600">{TIPO_PERSONA_LABELS[a.tipo_persona] || a.tipo_persona}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {sum.slice(0, 3).map((c, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100"
                                title={c.group}>
                            {c.label}
                          </span>
                        ))}
                        {sum.length > 3 && (
                          <span className="text-[10px] text-slate-400">+{sum.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 truncate max-w-[140px]"
                        data-testid={`anag-row-company-${a.id}`}>
                      {companyMap[a.company_id] || <span className="text-slate-300 italic">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 truncate max-w-[180px]">{a.email || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{a.telefono || a.mobile || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/anagrafiche/${a.id}`} className="text-blue-600 hover:underline text-xs flex items-center justify-end gap-1"
                            data-testid={`anag-row-${a.id}`}>
                        Apri <ArrowRight className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400">Nessuna anagrafica</td></tr>
              )}
            </tbody>
          </table>
        </StickyHScroll>
      </Card>

      {/* Dialog crea */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-4xl max-h-[92vh]">
          <DialogHeader>
            <DialogTitle>Nuova anagrafica</DialogTitle>
            <DialogDescription>
              Compila i dati e seleziona almeno 1 categoria. I campi contrassegnati con * sono obbligatori.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            {/* Sprint PRIV-SCOPE.3 — selettore azienda obbligatorio */}
            <div className="mb-4 pb-4 border-b">
              <Label className="text-xs flex items-center gap-1">
                Azienda di appartenenza <span className="text-rose-600">*</span>
              </Label>
              <Select
                value={form.company_id || ""}
                onValueChange={(v) => setForm({ ...form, company_id: v })}
              >
                <SelectTrigger className="w-full max-w-md" data-testid="anag-form-company">
                  <SelectValue placeholder="Seleziona azienda…" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) =>
                    <SelectItem key={c.id} value={c.id}>{c.name || c.nome_breve || c.ragione_sociale || c.id}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1 italic">
                L'anagrafica sarà visibile solo agli utenti con accesso a questa azienda.
              </p>
            </div>
            <AnagraficaDatiForm
              value={form}
              onChange={setForm}
              taxonomy={taxonomy}
              mode="create"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)} disabled={saving}>Annulla</Button>
            <Button onClick={() => submit(false)} disabled={saving} className="bg-blue-600 hover:bg-blue-700"
                    data-testid="anag-form-submit">
              {saving ? "Creazione..." : "Crea anagrafica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint 4D.1 (B1): modale duplicate CF */}
      <DuplicateCfDialog
        open={dupModal.open}
        existing={dupModal.existing}
        onClose={() => setDupModal({ open: false, existing: null })}
        onForceCreate={() => submit(true)}
        saving={saving}
        testIdPrefix="anag-form"
      />
    </div>
  );
}
